/**
 * Module dependencies.
 */

import { AppRoutes } from 'app/app-routes';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { I18nextProvider, TFunction, useTranslation } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { createAxiosClient } from 'app/core/utils/axios-client';
import { createQueryClient } from 'app/core/utils/react-query';
import React, { useCallback, useMemo } from 'react';
import RequestProvider, { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import i18n from 'app/core/locales/i18n';
import { useHackMobileBrowser } from './hooks/use-hack-mobile-browser';
import { StorageProvider } from './components/providers/storage-provider/storage-provider';
import { ConfigProvider, notification } from 'antd';
import { camelCase } from 'lodash';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CollapseMenuProvider } from './components/providers/collapse-menu-provider/collapse-menu-provider';
import { QueryParamsStorageProvider } from './components/providers/query-params-storage/query-params-storage';
import { GlobalStateProvider } from './components/providers/global-state-provider/global-state-provider';
import { syncLogout } from './hooks/app-requests/use-logout';
import { ExportsCountProvider } from './components/providers/exports-count-provider';

/**
 * Axios client.
 */

const axiosClient = createAxiosClient();

/**
 * Resolve error message.
 */

function resolveErrorMessage(translate: TFunction, error: any) {
  const { code: originalCode, groupCode, ...originalParams } = error?.response?.data?.data ?? {};
  let code = originalCode;
  let params = originalParams;

  if (!code && !groupCode && params?.[0]?.instancePath) {
    const {
      keyword,
      params: { type }
    } = params[0];

    switch (keyword) {
      case 'type': {
        code = 'typeError';
        params = {
          type,
          field: params[0].instancePath.replace('/', '')
        };

        break;
      }

      case 'phoneNumber': {
        code = 'phoneNumberError';
        params = {
          field: params[0].instancePath.replace('/', '')
        };
      }
    }
  }

  const messageTranslation = translate('common.errors.network.default.title');
  const message = translate([
    `common.errors.network.${code}.title`,
    `common.errors.network.${groupCode}.title`,
    `common.errors.network.${error?.response?.status}.title`,
    'common.errors.network.default.title'
  ]);

  const descriptionTranslation = translate('common.errors.network.default.description');
  const description = translate(
    [
      `common.errors.network.${code}.description`,
      `common.errors.network.${groupCode}.description`,
      `common.errors.network.${error.response?.status}.description`,
      'common.errors.network.default.description'
    ],
    params ?? {}
  );

  if (messageTranslation === message) {
    console.log(
      `Missing Message translation: ${JSON.stringify(
        [
          `common.errors.network.${code}.title`,
          `common.errors.network.${groupCode}.title`,
          `common.errors.network.${error?.response?.status}.title`,
          'common.errors.network.default.title'
        ],
        null,
        2
      )}`
    );
  }

  if (descriptionTranslation === description) {
    console.log(
      `Missing Description translation: ${JSON.stringify(
        {
          keys: [
            `common.errors.network.${code}.description`,
            `common.errors.network.${groupCode}.description`,
            `common.errors.network.${error.response?.status}.description`,
            'common.errors.network.default.description'
          ],
          params
        },
        null,
        2
      )}`
    );
  }

  return {
    message,
    description
  };
}

/**
 * Resolve success message.
 */

function resolveSuccessMessage(translate: TFunction, payload: any) {
  const { ui, data } = payload ?? {};
  const { entity, operation, hidden } = ui ?? {};

  if (hidden) {
    return null;
  }

  if (!entity || !operation) {
    console.warn(
      `Missing entity or operation: ${entity} | ${operation} => ${camelCase([entity, operation].join(' '))}`
    );
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log(
      'Success Message: ',
      `common.success.network.${camelCase([entity, operation].join(' '))}.title`,
      `common.success.network.${entity}.title`,
      'common.success.network.default.title'
    );
  }

  return {
    message: translate([
      `common.success.network.${camelCase([entity, operation].join(' '))}.title`,
      `common.success.network.${entity}.title`,
      'common.success.network.default.title'
    ]),
    description: translate(
      [
        `common.success.network.${camelCase([entity, operation].join(' '))}.description`,
        `common.success.network.${entity}.description`,
        'common.success.network.default.description'
      ],
      data ?? {}
    )
  };
}

/**
 * `QueryClient` component.
 */

function QueryClient({ children }: { children: JSX.Element }) {
  const [api, contextHolder] = notification.useNotification();
  const client = useRequestProvider();
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const showError = useCallback(
    (error: any) => {
      const { message, description } = resolveErrorMessage(translate, error);

      api.error({
        key: `error-${new Date().getTime()}`,
        message,
        description,
        placement: 'bottomLeft'
      });

      if (error.response?.status === 401 && error.response?.data?.data?.code === 'invalid_token') {
        syncLogout(client, navigate, false);
      }
    },
    [api, client, navigate, translate]
  );

  const showSuccess = useCallback(
    (data: any) => {
      const message = resolveSuccessMessage(translate, data);

      if (!message) {
        return;
      }

      api.success({
        key: `success-${new Date().getTime()}`,
        ...message,
        placement: 'bottomLeft'
      });
    },
    [api, translate]
  );

  const queryClient = useMemo(() => createQueryClient(showError, showSuccess), [showError, showSuccess]);

  return (
    <div className={'fix_notification'}>
      {contextHolder}

      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </div>
  );
}

/**
 * Export `App` component.
 */

export function App(): JSX.Element {
  useHackMobileBrowser();

  return (
    <>
      <GoogleOAuthProvider clientId={'188150150161-6af4otuc2uljn4d9oooegueqfl0ff9qi.apps.googleusercontent.com'}>
        <ConfigProvider>
          <StorageProvider>
            <I18nextProvider i18n={i18n}>
              <RequestProvider client={axiosClient}>
                <QueryParamsStorageProvider>
                  <GlobalStateProvider>
                    <BrowserRouter>
                      <QueryClient>
                        <CollapseMenuProvider>
                          <ExportsCountProvider>
                            <AppRoutes />
                          </ExportsCountProvider>
                        </CollapseMenuProvider>
                      </QueryClient>
                    </BrowserRouter>
                  </GlobalStateProvider>
                </QueryParamsStorageProvider>
              </RequestProvider>
            </I18nextProvider>
          </StorageProvider>
        </ConfigProvider>
      </GoogleOAuthProvider>
    </>
  );
}
